/**
 * Салбар, агуулах ҮҮСГЭХ · ЗАСАХ — нэг л маягт.
 *
 * Хоёр дуудагч:
 *   · «Тохиргоо → Салбар, агуулах» — тухайн табын төрлөөр түгжигдэнэ
 *   · «Агуулах» хуудас — `fixedType="WAREHOUSE"`
 *
 * ⚠️ `stores.type` нь салбар (RETAIL) ба агуулах (WAREHOUSE) хоёрыг
 *    ялгана — хоёр өөр хүснэгт БИШ, нэг хүснэгтийн хоёр төрөл. Тиймээс
 *    агуулах нэмэх нь «шинэ хүснэгт» биш, зүгээр л `type` түгжигдсэн
 *    энэ маягт.
 *
 * ⚠️ ХАРИУЦАГЧ нь `users.storeId`-ЭЭС ӨӨР. Тэр нь «энэ хүн хаана
 *    ажилладаг вэ» (олон хүн нэг салбарт), энэ нь «энэ салбарыг хэн
 *    хариуцдаг вэ» (нэгд нэг). Хариуцагч өөр салбарын ажилтан байж
 *    болно — түр орлон гүйцэтгэгч.
 *
 * ⚠️ Ажилтны жагсаалтыг `/reports/staff`-аас авна, `/ref/users`-ээс
 *    БИШ: сүүлийнх нь `setupStaff.view` (админ) шаарддаг тул агуулахын
 *    хэрэглэгч маягтыг нээхэд 403 өгнө.
 *
 * ⚠️ ХОЁР БАГАНА (`sm:`-ээс дээш). Нэг баганаар байхад газрын зурагтай
 *    хамт диалог ~900px өндөр болж, дэлгэцэнд багтахаа больж байв
 *    (`Modal` нь `sm:max-h-none` — гадна талаараа гүйдэг).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { normalizePhone } from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { searchAddress, addressAt, type GeoHit } from '../lib/geocode';
import type { Store } from '../lib/types';
import { Button, ErrorNote, Field, Input, Modal, Select, Textarea } from './ui';
import { MapView } from './map-view';

export const STORE_TYPE_LABEL: Record<Store['type'], string> = {
  RETAIL: 'Жижиглэн худалдаа',
  WAREHOUSE: 'Агуулах',
};

interface StaffPick {
  staff: { id: string; fullName: string; isActive: boolean; storeName: string | null }[];
}

export function StoreForm({ store, fixedType, onClose, onDone }: {
  store: Store | null;
  /** Өгвөл төрөл сонгох талбар гарахгүй — шинэ мөр яг энэ төрлөөр үүснэ */
  fixedType?: Store['type'];
  onClose: () => void;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const isNew = !store;
  const [code, setCode] = useState(store?.code ?? '');
  const [name, setName] = useState(store?.name ?? '');
  const [type, setType] = useState<Store['type']>(store?.type ?? fixedType ?? 'RETAIL');
  const [address, setAddress] = useState(store?.address ?? '');
  const [phone, setPhone] = useState(store?.phone ?? '');
  const [managerId, setManagerId] = useState(store?.managerId ?? '');
  const [lat, setLat] = useState<number | null>(store?.lat ?? null);
  const [lng, setLng] = useState<number | null>(store?.lng ?? null);

  const staff = useQuery({
    queryKey: ['staff-report'],
    queryFn: () => api<Envelope<StaffPick>>('/reports/staff'),
  });

  /** Хоёулаа эсвэл хоёулаа null — сервер ч мөн адил шаардана. */
  const coords = lat !== null && lng !== null ? { lat, lng } : { lat: null, lng: null };

  const save = useMutation({
    mutationFn: () => {
      const common = {
        name,
        address: address.trim() || null,
        phone: phone.trim() ? normalizePhone(phone) ?? phone.trim() : null,
        managerId: managerId || null,
        ...coords,
      };
      return isNew
        ? api('/ref/stores', {
            method: 'POST',
            body: { code: code.toUpperCase(), type, ...common },
          })
        : api(`/ref/stores/${store.id}`, { method: 'PATCH', body: common });
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['branches'] }),
        qc.invalidateQueries({ queryKey: ['warehouses'] }),
        qc.invalidateQueries({ queryKey: ['ref-stores'] }),
      ]);
      onDone();
    },
  });

  const noun = (fixedType ?? type) === 'WAREHOUSE' ? 'агуулах' : 'салбар';
  const title = isNew
    ? `Шинэ ${noun}`
    : `${store.code} — ${store.name}`;

  const managers = (staff.data?.data.staff ?? []).filter((s) => s.isActive);

  return (
    <Modal
      title={title}
      subtitle={isNew ? undefined : STORE_TYPE_LABEL[store.type]}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => save.mutate()} disabled={!name || (isNew && !code) || save.isPending}>
            {save.isPending ? 'Хадгалж байна…' : 'Хадгалах'}
          </Button>
        </>
      }
    >
      <div className="grid items-start gap-x-4 gap-y-3 sm:grid-cols-2">
        {/* ─────────── Зүүн: бүртгэл ─────────── */}
        <div className="flex flex-col gap-3">
          {isNew ? (
            <>
              <Field label="Код">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder={noun === 'агуулах' ? 'WH2' : 'S4'}
                  className="num"
                />
              </Field>
              {fixedType ? null : (
                <Field label="Төрөл">
                  <Select
                    value={type}
                    onChange={(v) => setType(v as Store['type'])}
                    options={[
                      { value: 'RETAIL', label: STORE_TYPE_LABEL.RETAIL },
                      { value: 'WAREHOUSE', label: STORE_TYPE_LABEL.WAREHOUSE },
                    ]}
                  />
                </Field>
              )}
            </>
          ) : null}

          <Field label="Нэр">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>

          <Field label="Хариуцагч ажилтан">
            <Select
              value={managerId}
              onChange={setManagerId}
              placeholder={staff.isPending ? 'Ачаалж байна…' : 'Хариуцагчгүй'}
              options={managers.map((s) => ({
                value: s.id,
                label: s.storeName ? `${s.fullName} · ${s.storeName}` : s.fullName,
              }))}
            />
          </Field>

          <Field label="Утас">
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="99112233" className="num" />
          </Field>
        </div>

        {/* ─────────── Баруун: хаяг + зураг ─────────── */}
        <AddressPicker
          address={address}
          onAddress={setAddress}
          lat={lat}
          lng={lng}
          code={code || store?.code || '?'}
          onCoords={(a, b) => { setLat(a); setLng(b); }}
        />
      </div>

      {save.error ? <ErrorNote>{(save.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

/**
 * Хаяг ба байршил — үсгээр бичих ба зураг дээр дарах ХОЁУЛАА.
 *
 * ⚠️ Nominatim-ийн журам (секундэд ≤1 хүсэлт) тул хайлт нь ЗӨВХӨН товч
 *    дарахад явна, бичих бүрд БИШ. Автомат болговол хаяг бөглөх бүрд
 *    20-30 хүсэлт явж, IP хаагдана.
 */
function AddressPicker({
  address, onAddress, lat, lng, code, onCoords,
}: {
  address: string;
  onAddress: (v: string) => void;
  lat: number | null;
  lng: number | null;
  code: string;
  onCoords: (lat: number, lng: number) => void;
}) {
  const [hits, setHits] = useState<GeoHit[] | null>(null);
  const [busy, setBusy] = useState<'search' | 'reverse' | null>(null);

  async function runSearch() {
    setBusy('search');
    setHits(await searchAddress(address));
    setBusy(null);
  }

  async function pullAddress() {
    if (lat === null || lng === null) return;
    setBusy('reverse');
    const found = await addressAt(lat, lng);
    if (found) onAddress(found);
    setBusy(null);
  }

  return (
    <div className="flex flex-col gap-3">
      <Field label="Хаяг">
        <div className="flex flex-col gap-2">
          <Textarea
            rows={2}
            value={address}
            onChange={(e) => { onAddress(e.target.value); setHits(null); }}
            placeholder="Сүхбаатар дүүрэг, 1-р хороо, Энх тайваны өргөн чөлөө 12"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              onClick={() => void runSearch()}
              disabled={address.trim().length < 3 || busy !== null}
            >
              {busy === 'search' ? 'Хайж байна…' : 'Хаягаар олох'}
            </Button>
            {lat !== null && lng !== null ? (
              <Button variant="ghost" onClick={() => void pullAddress()} disabled={busy !== null}>
                {busy === 'reverse' ? 'Уншиж байна…' : 'Зургаас хаяг авах'}
              </Button>
            ) : null}
          </div>

          {hits?.length === 0 ? (
            <p className="text-[11.5px] text-ink-3">
              Олдсонгүй. Зураг дээр шууд дарж тэмдэглэж болно.
            </p>
          ) : null}

          {hits && hits.length > 0 ? (
            <ul className="flex flex-col gap-1 rounded-well border border-hair bg-well p-1.5">
              {hits.map((h) => (
                <li key={`${h.lat},${h.lng}`}>
                  <button
                    type="button"
                    onClick={() => { onCoords(h.lat, h.lng); onAddress(h.address); setHits(null); }}
                    className="w-full rounded-lg px-2.5 py-1.5 text-left text-[12px] text-ink-2
                      transition hover:bg-panel"
                  >
                    {h.address}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </Field>

      <Field label="Байршил">
        <div className="flex flex-col gap-2">
          <MapView
            points={
              lat !== null && lng !== null
                ? [{ id: 'here', code, label: address || code, lat, lng }]
                : []
            }
            onPick={onCoords}
            height="h-44"
            hint="Зураг дээр дарж тэмдэглэнэ. Зөөх бол дахин дарна."
          />
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={lat === null ? '' : String(lat)}
              onChange={(e) => {
                const v = e.target.value.trim();
                if (v !== '' && lng !== null) onCoords(Number(v), lng);
              }}
              placeholder="Өргөрөг"
              inputMode="decimal"
              aria-label="Өргөрөг"
              className="num max-w-32"
            />
            <Input
              value={lng === null ? '' : String(lng)}
              onChange={(e) => {
                const v = e.target.value.trim();
                if (v !== '' && lat !== null) onCoords(lat, Number(v));
              }}
              placeholder="Уртраг"
              inputMode="decimal"
              aria-label="Уртраг"
              className="num max-w-32"
            />
          </div>
        </div>
      </Field>
    </div>
  );
}
